import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import { Badge } from '../../../components/ui/badge';
import { useAdminIssuesUsers } from '../../../hooks/useAdminIssuesUsers';

interface UserSearchSelectProps {
  serviceDeskId: number;
  value?: string[]; // selected accountIds
  placeholder?: string;
  onChange: (accountIds: string[]) => void;
}

// Debounced async multi-select: searches Jira users for the current service desk
// and emits the picked accountIds as badges. No shared combobox primitive exists,
// so this is a small self-contained input + results list.
export const UserSearchSelect = ({
  serviceDeskId,
  value,
  placeholder,
  onChange,
}: UserSearchSelectProps) => {
  const { t } = useTranslation();
  const selected = value ?? [];
  const [text, setText] = useState('');
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  // accountId → displayName, so removed/reselected badges keep a readable label.
  const [labels, setLabels] = useState<Record<string, string>>({});
  const containerRef = useRef<HTMLDivElement>(null);

  // Clear the search box when the filter is emptied externally (reset, SD change).
  useEffect(() => {
    if (selected.length === 0) {
      setText('');
      setQuery('');
    }
  }, [value]);

  useEffect(() => {
    const handle = setTimeout(() => setQuery(text.trim()), 300);
    return () => clearTimeout(handle);
  }, [text]);

  // Close the dropdown on outside click.
  useEffect(() => {
    const onClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  const { data: users = [], isFetching } = useAdminIssuesUsers(serviceDeskId, query);
  const showResults = open && query.length >= 2;

  const add = (accountId: string, displayName: string) => {
    if (!selected.includes(accountId)) {
      setLabels((m) => ({ ...m, [accountId]: displayName || accountId }));
      onChange([...selected, accountId]);
    }
    setText('');
    setQuery('');
  };

  const remove = (accountId: string) => onChange(selected.filter((id) => id !== accountId));

  return (
    <div ref={containerRef} className="relative">
      <div className="flex flex-wrap gap-1 items-center min-h-9 w-full rounded-md border border-input bg-transparent px-2 py-1 focus-within:ring-1 focus-within:ring-ring">
        {selected.map((id) => (
          <Badge key={id} variant="secondary" className="text-xs px-1.5 py-0">
            {labels[id] || id}
            <button
              type="button"
              onClick={() => remove(id)}
              className="ml-1 hover:text-red-500"
              aria-label={t('Remove')}
            >
              <X className="h-3 w-3" />
            </button>
          </Badge>
        ))}
        <input
          className="flex-1 min-w-[80px] bg-transparent text-sm outline-none px-1 py-0.5 placeholder:text-muted-foreground"
          placeholder={selected.length ? '' : placeholder ?? t('Search…')}
          value={text}
          onChange={(e) => {
            setText(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
        />
      </div>

      {showResults && (
        <div className="absolute z-50 mt-1 w-full rounded-md border border-input bg-white shadow-md max-h-60 overflow-auto">
          {isFetching && users.length === 0 ? (
            <div className="px-3 py-2 text-sm text-gray-500">{t('Searching…')}</div>
          ) : users.length === 0 ? (
            <div className="px-3 py-2 text-sm text-gray-500">{t('No result')}</div>
          ) : (
            users.map((user) => {
              const isSelected = selected.includes(user.accountId);
              return (
                <button
                  key={user.accountId}
                  type="button"
                  // onMouseDown fires before blur, so the click isn't lost.
                  onMouseDown={(e) => {
                    e.preventDefault();
                    add(user.accountId, user.displayName);
                  }}
                  disabled={isSelected}
                  className="block w-full text-left px-3 py-2 text-sm hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {user.displayName || user.accountId}
                </button>
              );
            })
          )}
        </div>
      )}
    </div>
  );
};
